// Формат changelog:
// - Блоки в интерфейсе сортируются по дате по убыванию, затем по версии по убыванию.
// - `version` можно не указывать, но первый блок с `version` задает базовую версию приложения для сборки.
// - `commit` — короткий хэш коммита с изменением; значение `AUTO` заменяет скрипт `npm run changelog:finalize`,
//   который сохраняет замену отдельным commit.
// - `build` — количество коммитов для этой версии; значение `AUTO` также заменяет скрипт финализации.
// - `roles` у пункта можно не указывать; без `roles` пункт виден всем пользователям.
// - `fullText` / `fullTextEn` у пункта — полный текст для страницы истории изменений.
//   Эти поля можно задать и у выпуска целиком: при раскрытии они заменяют список его пунктов.
//   Полный текст выпуска виден всем, кому доступен выпуск; сведения для отдельных ролей размещайте в пунктах.
//   В уведомлениях по-прежнему используется `description` / `descriptionEn`.
//   Если полный текст не задан, на странице показывается description. Форматирование одинаковое.
// - Поддерживаемые роли: `authenticated`, `club-admin`, `superadmin`.
// - `description` поддерживает многострочные template strings и безопасное подмножество markdown:
//   **жирный**, *курсив*, `код`, [внешняя ссылка](https://example.com), [внутренняя ссылка](/example),
//   {success:зеленый текст}, {warning:желтый текст}, {danger:красный текст},
//   {icon:bi-bell} — иконка Bootstrap Icons, базовый class `bi` добавится автоматически.
//   Цвет иконки можно указать после `|`: {icon:bi-bell|success}, {icon:bi-bell|#198754},
//   {icon:bi-bell|rgb(25, 135, 84)} или {icon:bi-bell|rgba(25, 135, 84, 0.8)}.
//   Алиасы цветов: success, warning, danger, primary, secondary, muted.
export default [
  {
    version: '0.5.3', commit: 'b8d6ed8', build: '569', date: '2026-11-01',
    title: 'Небольшие визуальные правки',
    titleEn: 'Minor visual improvements',
    items: [
      {
        text: 'Небольшие визуальные правки',
        textEn: 'Minor visual improvements',
      },
    ],
  },
  {
    version: '0.5.3', commit: 'f3c7bea', build: '560', date: '2026-10-31',
    title: 'Снятие участников и работа с жеребьёвкой',
    titleEn: 'Participant withdrawal and pairings improvements',
    items: [
      {
        text: 'Снятие и возврат участников',
        textEn: 'Withdrawing and restoring participants',
        description: 'При снятии и возврате участников результаты несыгранных партий обновляются автоматически. В круговике можно выбрать тур, начиная с которого действует изменение. Подробнее — в статье [«Снятие и возврат в круговике»](/docs/tournaments/participants/#snyatie-i-vozvrat-v-krugovike).',
        descriptionEn: 'Unplayed game results are updated automatically when participants are withdrawn or restored. In round robin, you can choose the round from which the change takes effect. Learn more in [Withdrawal and restoration in round robin](/docs/en/tournaments/participants/#snyatie-i-vozvrat-v-krugovike).',
      },
      {
        text: 'Открытые блоки жеребьёвки',
        textEn: 'Expanded pairing groups',
        description: 'После обновления страницы открытые и закрытые блоки жеребьёвки сохраняют своё состояние.',
        descriptionEn: 'Expanded and collapsed pairing groups retain their state after refreshing the page.',
      },
    ],
    fullText: `{icon:bi-person-dash|warning} {warning:Снятие и возврат участников}

При снятии игрока или команды через список участников в несыгранных партиях автоматически назначается техническое поражение с окончанием «Снятие». Если к туру встречи сняты оба соперника, партия получает окончание «Снятие обоих» без очков каждому.

В круговике при снятии и возврате появляется поле **«Начиная с тура»**. По умолчанию предлагается первый тур с незавершёнными партиями; номер можно изменить. Это относится и к круговой квалификации смешанного турнира. **Партии предыдущих туров и обычные результаты сыгранных партий сохраняются.**

При возврате начиная с выбранного тура отменяется снятие вернувшегося участника. Если был снят только он, результат и окончание очищаются — партию можно сыграть. Если были сняты оба, вернувшийся получает техническую победу из-за снятия соперника. Возврат второго соперника с этого же тура очищает и этот результат.

Например, если Петя снят со 2-го тура, а Вася — с 5-го, техническая победа Васи над Петей в 3-м туре сохраняется. Но в их встрече в 7-м туре будет «Снятие обоих» без очков.

Подробнее — [«Снятие и возврат в круговике»](/docs/tournaments/participants/#snyatie-i-vozvrat-v-krugovike).

{icon:bi-layout-text-sidebar|warning} {warning:Сохранение открытых блоков жеребьёвки}

Раскрытые и свёрнутые блоки жеребьёвки запоминаются в браузере отдельно для каждого турнира и режима группировки — по турам или кругам. После обновления страницы восстанавливаются те же открытые блоки. Если вы закрыли все блоки, они останутся закрытыми.`,
    fullTextEn: `{icon:bi-person-dash|warning} {warning:Withdrawing and restoring participants}

Withdrawing a player or team from the participant list automatically assigns a technical loss marked as a withdrawal in unplayed games. If both opponents are withdrawn by the round of their meeting, the game is marked as withdrawal of both, with no points for either player.

In round robin, withdrawing or restoring a participant opens a **Starting from round** field. The default is the first round with unfinished games; you can change the number. This also applies to round-robin qualification in mixed tournaments. **Earlier rounds and regular results of games already played remain unchanged.**

Restoring a participant removes their withdrawal from the selected round onward. If only that participant was withdrawn, the result and termination are cleared so the game can be played. If both were withdrawn, the returning participant receives a technical win because the opponent remains withdrawn. Restoring the second opponent from the same round clears that result as well.

For example, if Peter is withdrawn from round 2 and Alex from round 5, Alex keeps the technical win against Peter in round 3. Their meeting in round 7 is marked as withdrawal of both, with no points awarded.

Learn more in [Withdrawal and restoration in round robin](/docs/en/tournaments/participants/#snyatie-i-vozvrat-v-krugovike).

{icon:bi-layout-text-sidebar|warning} {warning:Remembering expanded pairing groups}

Expanded and collapsed pairing groups are remembered in your browser separately for each tournament and grouping mode — by round or cycle. Refreshing the page restores the same open groups. If you close every group, they remain closed.`,
  },
  {
    version: '0.5.2', commit: '3fcc318', build: '556', date: '2026-10-30',
    title: 'Личные встречи и рассадка',
    titleEn: 'Head-to-head history and seating',
    items: [
      {
        text: 'Личные встречи',
        textEn: 'Head-to-head history',
        description: 'В карточках партий появился блок «Личные встречи»: победы, ничьи и поражения соперников в текущем виде шахмат и контроле времени — всего и при текущих цветах. Подробнее — в статье [«Жеребьёвка турнира»](/docs/tournaments/pairings/#lichnye-vstrechi).',
        descriptionEn: 'Game cards now show head-to-head history: the opponents’ wins, draws and losses in the current chess variant and time control, both overall and with the current colors. Learn more in [Tournament pairings](/docs/en/tournaments/pairings/#lichnye-vstrechi).',
      },
      {
        text: 'Назначение столов в швейцарке',
        textEn: 'Swiss table assignment',
        description: 'Для всех алгоритмов швейцарской жеребьёвки применяется единый порядок столов: по очкам лидера пары, затем по сумме очков соперников и посеву. Bye не занимает игровой стол.',
        descriptionEn: 'All Swiss pairing engines now use the same table order: the higher-scoring opponent’s points, then the opponents’ combined points and seeding. Byes do not occupy playing tables.',
      },
      {
        text: 'Прочие исправления',
        textEn: 'Other fixes',
        description: 'Прочие небольшие исправления.',
        descriptionEn: 'Other minor fixes.',
      },
    ],
  },
  {
    version: '0.5.1', commit: 'e47c261', build: '546', date: '2026-10-26',
    title: 'Генератор Chess960',
    titleEn: 'Chess960 generator',
    items: [
      {
        text: 'Генератор 960 и расстановки туров',
        textEn: '960 generator and round positions',
        description: 'Появился генератор 960 на отдельной странице и возможность выбирать и сохранять расстановку каждого тура в турнирах Chess960. Подробнее — в статье [«Генератор 960»](/docs/tournaments/chess960-generator/).',
        descriptionEn: 'A standalone 960 generator is now available, along with the ability to choose and save a starting position for each round in Chess960 tournaments. Learn more in [960 generator](/docs/en/tournaments/chess960-generator/).',
      },
    ],
    fullText: `{icon:bi-grid-3x3|warning} {warning:Генератор Chess960}

**1. Отдельная публичная страница.** В меню «Шахматы» появился пункт «Генератор 960». Выбирайте случайную расстановку или вводите номер позиции. Вход в аккаунт и созданный турнир не нужны.

**2. Номера позиций и FEN.** Доступны все 960 стартовых позиций по нумерации Шарнагла — от 0 до 959. Один номер всегда соответствует одной расстановке; №518 — обычная начальная позиция. На странице можно скопировать Shredder-FEN, чтобы поделиться позицией или использовать её в турнире.

**3. Расстановка для каждого тура.** При создании или редактировании турнира Chess960 включите «Показывать генератор Chess960 в каждом туре». После формирования пар нажмите {icon:bi-arrow-repeat}, чтобы сразу выбрать и сохранить случайную расстановку для тура. Чтобы указать конкретную позицию, откройте настройки кнопкой {icon:bi-gear}, введите номер или FEN начальной позиции и нажмите «Сохранить».

**4. Позиция сохраняется в турнире.** Сервис предполагает использование одной общей расстановки для всех партий одного тура. Для другого тура рекомендуется сгенерировать новую расстановку. Если ваш формат отличается, вы можете просто использовать генератор на отдельной странице и проводить игры в удобном вам формате.

Расстановка, сгенерированная внутри турнира, сохраняется в нём и остаётся в соответствующем туре. Выбор на отдельной публичной странице сам по себе не меняет позиции турнира.

**5. Отображение в жеребьёвке и рассадке.** В раскрытом списке жеребьёвки расстановка показана перед партиями соответствующего тура, в том числе внутри общего списка круга в круговике. Участники видят позицию, а кнопки изменения доступны пользователям с правом управления жеребьёвкой.

Подробная инструкция — [«Генератор 960»](/docs/tournaments/chess960-generator/).`,
    fullTextEn: `{icon:bi-grid-3x3|warning} {warning:Chess960 generator}

**1. A standalone public page.** The Chess menu now includes 960 generator. Generate a random setup or enter a position number. No sign-in or tournament is required.

**2. Position numbers and FEN.** All 960 starting positions use Scharnagl numbering from 0 to 959. Each number always identifies the same arrangement; #518 is the standard starting position. Copy Shredder-FEN from the page to share a position or use it in a tournament.

**3. A position for every round.** When creating or editing a Chess960 tournament, enable “Show the Chess960 generator in every round”. Once pairings exist, click {icon:bi-arrow-repeat} to immediately choose and save a random position for the round. To specify a particular position, open settings with {icon:bi-gear}, enter its number or starting-position FEN and click Save.

**4. Saved with the tournament.** The service assumes one shared starting position for all games in a round. We recommend generating a new position for each subsequent round. If your format differs, you can use the generator on the standalone page and organize games in the way that suits you.

A position generated inside a tournament is saved with that tournament and remains attached to its round. Choosing a position on the standalone public page does not change tournament positions.

**5. Pairings and seating views.** In the expanded pairings list, the position appears before the games of its round, including within a complete round-robin cycle. Participants can view the position; editing controls are available to users with permission to manage pairings.

For step-by-step instructions, see [960 generator](/docs/en/tournaments/chess960-generator/).`,
  },
  {
    version: '0.5.0', commit: 'b5d01c7', build: '534', date: '2026-10-26',
    title: 'Плей-офф, итоговые таблицы и марсельские шахматы',
    titleEn: 'Playoffs, final standings and Marseillais chess',
    items: [
      {
        text: 'Плей-офф с неполной сеткой',
        textEn: 'Playoffs with incomplete brackets',
        description: 'Число участников больше не обязательно должно быть степенью двойки. Часть участников проходит в следующую стадию автоматически.',
        descriptionEn: 'The participant count no longer has to be a power of two. Some participants advance directly to the next stage.',
      },
      {
        text: 'Сетка плей-офф в жеребьёвке и ввод результатов',
        textEn: 'Playoff bracket view and result entry in Pairings',
        description: 'Переключайтесь между списком и сеткой. Откройте встречу из сетки, внесите результаты и сохраните изменения.',
        descriptionEn: 'Switch between the list and bracket. Open a match from the bracket, enter results and save your changes.',
      },
      {
        text: 'Вкладка «Итоги»',
        textEn: 'Final results tab',
        description: 'После завершения турнира доступны итоговые места и общая статистика квалификации и плей-офф.',
        descriptionEn: 'After the tournament is completed, view final places and combined qualification and playoff statistics.',
      },
      {
        text: 'Добавлены марсельские шахматы',
        textEn: 'Marseillais chess added',
        description: 'Новый вид шахмат с отдельным рейтингом для каждого контроля времени.',
        descriptionEn: 'A new chess variant with separate ratings for each time control.',
      },
      {
        text: 'Прочие небольшие улучшения и исправления ошибок',
        textEn: 'Other minor improvements and bug fixes',
      },
    ],
    fullText: `{icon:bi-tools|warning} {warning:Доработки}

**1. Плей-офф с неполной сеткой.** Число участников больше не обязательно должно быть степенью двойки. Часть участников играет предварительную стадию, остальные проходят дальше автоматически.

**2. Отображение свободных проходов.** Участники, не играющие предварительную стадию, объединены в блок «Уже вышли в следующий раунд». Они не занимают столы в рассадке и не требуют ввода результата. Свободный проход не считается сыгранной партией.

**3. Проверка состава команд.** Перед стартом плей-офф проверяется возможность собрать полные команды в каждой сетке, если выбран режим случайных команд. Например, из нечётного числа участников нельзя собрать команды по два человека. Если остаются лишние игроки, выводится пояснение: изменить границы зоны или вручную исключить участников.

**4. Полная сетка до финала.** Теперь сразу рисуется полная сетка, включая будущие стадии. Участники со свободным проходом заранее показываются в следующей стадии, хотя партии ещё не созданы.

**5. Переключение вида жеребьёвки плей-офф.** Можно выбрать сетку или список. Выбранный режим запоминается. Сетка показывает пары в виде сетки плей-офф, список — как раньше, в стандартном виде жеребьёвки.

**6. Ввод результатов через сетку.** В разделе «Жеребьёвка» можно вводить результат и в режиме «Сетка». Нажмите на пару — откроется окно встречи. В нём результат переключается нажатием: победа первой стороны → победа второй → ничья → очистка. Изменения выделяются жёлтым и применяются кнопкой «Сохранить». Поддерживаются общий командный результат и ввод по доскам.

**7. Вкладка «Итоги».** После завершения турнира с плей-офф в разделе «Таблица» доступна таблица итоговых мест и суммарной статистики за весь турнир, с экспортом картинки.

**8. Очки в экспорте.** На экспортируемой картинке очки теперь отображаются в формате «4 / 5» — набранные очки из возможных. BYE учитывается в максимуме только при начисленном одном очке. Нулевые пропуски и свободные проходы плей-офф исключены.

**9. Подсветка лидеров сезона после фильтрации.** Призовые цвета получают лучшие среди оставшихся игроков по сезонным местам. Это позволяет смотреть призёров в конкретной категории или с определённой меткой. Сортировка столбцов меняет расположение строк, но сохраняет их цвет: лидеров видно при сортировке по любому столбцу. При фильтрации остаётся столбец с итоговыми местами среди всех участников сезона.

**10. Добавлены марсельские шахматы.** Новый вид доступен при создании турнира и использует обычный индивидуальный формат — один на один. Рейтинги ведутся отдельно от других видов шахмат для каждого контроля времени.

{icon:bi-bug|success} {success:Багофиксы}

**1. Ошибка запуска плей-офф.** После последнего обновления появилась ошибка запуска плей-офф: матчи создавались в базе, но не отображались, а плей-офф не начинался. Проблема устранена.

**2. Ошибка при завершении турнира.** Похожая ошибка появилась при завершении турнира. Она тоже исправлена.

**3. Командные места.** Исправлены распределение мест напарников в командных турнирах, включая вылетевших на предыдущих стадиях, и подписи нижней сетки. При семи парных командах используются места 1–7, а не 1–14. В старых завершённых турнирах кэш останется рассчитанным по прежним правилам. Если места отображаются неверно, пересчитайте кэш турнира в админке.

**4. Визуальные исправления сетки.** Добавлено отображение цвета, которым играл игрок в одиночной партии серии плей-офф.

**5. Оформление.** В рассадке на фоне было статичное изображение. Теперь используется аватар клуба. До старта плей-офф показывается оформленное пустое состояние с загрузчиком.`,
    fullTextEn: `{icon:bi-tools|warning} {warning:Improvements}

**1. Playoffs with incomplete brackets.** The participant count no longer has to be a power of two. Some participants play a preliminary stage, while others advance automatically.

**2. Free advances.** Participants who skip the preliminary stage are grouped under “Already advanced to the next round”. They do not occupy seating tables or require a result. A free advance does not count as a played game.

**3. Team lineup validation.** Before random-team playoffs start, each bracket is checked for complete teams. For example, an odd number of players cannot form two-player teams. If players remain unassigned, the service explains how to adjust zone boundaries or exclude participants manually.

**4. Complete bracket through to the final.** The entire bracket, including future stages, is displayed immediately. Participants with free advances appear in the next stage before those games are created.

**5. Playoff pairing views.** Choose between a bracket and a list. The selected view is remembered. The bracket shows the playoff structure; the list retains the familiar pairing layout.

**6. Result entry through the bracket.** On the Pairings tab, results can also be entered in bracket view. Click a pairing to open the match window. Click a result to cycle through: first side wins → second side wins → draw → clear. Changes are highlighted in yellow and applied with Save. Both shared team results and per-board entry are supported.

**7. Final results tab.** Once a tournament with playoffs is completed, the Standings tab offers final places and combined statistics for the whole tournament, with image export.

**8. Points in exports.** Exported images now show points as “4 / 5”: points scored out of possible points. A BYE counts towards the maximum only when one point was awarded. Zero-point skips and playoff free advances are excluded.

**9. Season leader highlighting after filtering.** Podium colors identify the best remaining players by season place, making it easier to view leaders within a category or label. Sorting columns changes row order but preserves the colors, so leaders remain visible regardless of sorting. The overall season place column is retained when filters are applied.

**10. Marseillais chess added.** The new variant is available when creating a tournament and uses the usual one-on-one individual format. Ratings are maintained separately from other chess variants for each time control.

{icon:bi-bug|success} {success:Bug fixes}

**1. Starting playoffs.** An issue introduced in the previous update created matches in the database without displaying them or starting playoffs. This has been fixed.

**2. Completing tournaments.** A similar error when completing a tournament has also been fixed.

**3. Team places.** Corrected teammates’ places, including those eliminated in earlier stages, and lower-bracket labels. Seven two-player teams use places 1–7 rather than 1–14. Previously completed tournaments retain their existing cached standings. If places are incorrect, rebuild the tournament cache in the admin panel.

**4. Bracket visuals.** A player’s piece color is now shown for a single-game playoff series.

**5. Presentation.** The seating background now uses the club avatar instead of a fixed image. Before playoffs start, a styled empty state with a loader is displayed.`,
  },
  {
    version: '0.4.3', commit: 'eb619b1', build: '529', date: '2026-10-24',
    title: 'Небольшие исправления',
    titleEn: 'Minor fixes',
    items: [
      {
        text: 'Небольшие исправления в режиме плей-офф и круговой системе.',
        textEn: 'Minor fixes for playoffs and round-robin tournaments.',
      },
    ],
  },
  {
    version: '0.4.2', commit: '49b80c7', build: '523', date: '2026-10-24',
    title: 'Небольшие визуальные правки',
    titleEn: 'Minor visual improvements',
    items: [
      {
        text: 'Небольшие визуальные правки: отображение рейтингов и прочее.',
        textEn: 'Minor visual improvements: display of ratings and more.',
      },
    ],
  },
  {
    version: '0.4.1', commit: '42e3cfb', build: '517', date: '2026-10-23',
    title: 'Шаблоны турниров',
    titleEn: 'Tournament templates',
    items: [
      {
        text: 'Создавайте турниры с готовыми настройками',
        textEn: 'Create tournaments with saved settings',
        roles: ['club-admin'],
        description: 'Сохраняйте привычные настройки турнира в шаблон и выбирайте его на первом шаге создания. Подробнее — в статье [«Шаблоны турниров»](/docs/tournaments/templates/).',
        descriptionEn: 'Save your usual tournament settings as a template and select it on the first step of tournament creation. Learn more in [Tournament templates](/docs/en/tournaments/templates/).',
      },
    ],
  },
  {
    version: '0.4.0', commit: 'f56471a', build: '488', date: '2026-10-21',
    title: 'Игроки в нескольких клубах',
    titleEn: 'Players in multiple clubs',
    items: [
      {
        text: 'Общий профиль игрока и согласование изменений',
        textEn: 'Shared player profiles and change approvals',
        roles: ['club-admin'],
        description: 'Игрок может состоять и играть в нескольких клубах с одним профилем, сохраняя рейтинг и историю выступлений. При добавлении в турнир можно найти игрока другого клуба и добавить его в свой, не создавая дубликат. Изменение общих данных такого игрока — имени, фамилии и звания FIDE — требует согласия всех его клубов. Предложите исправление: администраторы других клубов получат уведомление и смогут принять запрос или отказать. Подробнее — в статье [«Согласование изменений игрока»](/docs/players/profile-changes/).',
        descriptionEn: 'A player can belong to and play in multiple clubs using one profile, keeping their rating and playing history. When adding participants to a tournament, you can find a player from another club and add them to yours without creating a duplicate. Changes to shared player details — first name, last name and FIDE title — require approval from all their clubs. Propose a correction: administrators of the other clubs will receive a notification and can accept or reject the request. Learn more in [Player profile change approvals](/docs/en/players/profile-changes/).',
      },
    ],
  },
  {
    version: '0.3.0', commit: '9908203', build: '471', date: '2026-10-20',
    title: 'Главная страница клуба',
    titleEn: 'Club homepage',
    items: [
      {
        text: 'У каждого клуба появилась главная страница',
        textEn: 'Every club now has a homepage',
        description: 'Знакомьтесь с клубом, выбирайте ближайший турнир и следите за результатами и успехами игроков. На странице собраны информация о клубе, встречи, сезон, рейтинги, места проведения и контакты организаторов. Подробнее — в статье [«Страница клуба»](/docs/clubs/page/).',
        descriptionEn: 'Get to know a club, find an upcoming tournament and follow results and player achievements. The page brings together club information, meetings, the season, ratings, venues and organizer contacts. Learn more in [Club page](/docs/en/clubs/page/).',
      },
      {
        text: 'Настройте главную страницу своего клуба',
        textEn: 'Customize your club homepage',
        roles: ['club-admin'],
        description: 'Теперь вы можете управлять содержанием и внешним видом главной страницы своего клуба. В настройках добавьте описание, выберите логотип и оформление названия, заполните приветственную карточку с иконками или фотографиями и укажите контакты для связи. В разделе «Шаблоны → Места» можно добавить фотографии площадок. Результаты турниров и статистика игроков появляются автоматически. Примеры и советы — в статье [«Настройки клуба»](/docs/roles/club-settings/).',
        descriptionEn: 'You can now control the content and appearance of your club homepage. In settings, add a description, choose a logo and name styling, create a welcome card with icons or photos, and provide contact details. Add venue photos under Templates → Places. Tournament results and player statistics appear automatically. See [Club settings](/docs/en/roles/club-settings/) for examples and tips.',
      },
    ],
  },
  {
    version: '0.2.3', commit: 'fd92ea0', build: '459', date: '2026-10-15',
    title: 'Места проведения и карта клубов',
    titleEn: 'Venues and club map',
    items: [
      {
        text: 'Места клуба и выбор места проведения турнира',
        textEn: 'Club places and tournament venues',
        roles: ['club-admin'],
        description: 'Сохраняйте заведения из Google Places в разделе «Шаблоны → Места» административной панели клуба и выбирайте их для турниров. Подробнее — в [инструкции по местам клуба](/docs/roles/control-panel/#mesta).',
        descriptionEn: 'Save Google Places venues in Templates → Places in the club control panel and select them for tournaments. See the [club places guide](/docs/en/roles/control-panel/#mesta).',
      },
      {
        text: 'Место проведения на странице и карточках турнира',
        textEn: 'Venues on tournament pages and cards',
        description: 'Место проведения теперь видно на странице турнира и в карточках на главной, в поиске и списке турниров. Ссылка на странице турнира открывает место в Google Maps.',
        descriptionEn: 'Venues now appear on tournament pages and cards on the home page, in search and in tournament lists. The venue link on the tournament page opens Google Maps.',
      },
      {
        text: 'Интерактивная карта клубов на главной',
        textEn: 'Interactive club map on the home page',
        description: 'На карте показаны места клубов, а если места не добавлены — их города. Нажмите на маркер, чтобы посмотреть карточку клуба, или на название города рядом с картой, чтобы перейти к поиску.',
        descriptionEn: 'The map shows club venues, or club cities when no venues have been added. Click a marker to view a club card, or a city name beside the map to search for it.',
      },
    ],
  },
  {
    version: '0.2.3',
    commit: '934bc03',
    build: '445',
    date: '2026-10-13',
    title: 'Номинации турниров',
    titleEn: 'Tournament nominations',
    items: [
      {
        text: 'Достижения игроков рядом с турнирной таблицей',
        textEn: 'Player achievements beside the standings',
        roles: ['club-admin'],
        description: 'В турнирах появились номинации: сервис автоматически определяет победителей и показывает их достижения в карточках рядом с таблицей. Администраторы могут создавать шаблоны в разделе «Шаблоны», настраивать условия и выбирать номинации для каждого турнира. Подробнее — в статье [«Номинации»](/docs/roles/nominations/).',
        descriptionEn: 'Tournaments now support nominations: the service automatically determines winners and displays their achievements in cards beside the standings. Administrators can create templates in the Templates section, configure eligibility and select nominations for each tournament. Learn more in [Nominations](/docs/en/roles/nominations/).',
      },
    ],
  },
  {
    version: '0.2.3',
    commit: '7ba63f7',
    build: '433',
    date: '2026-10-12',
    title: 'Обновление швейцарской жеребьёвки',
    titleEn: 'Swiss pairing update',
    items: [
      {
        text: 'Швейцарская жеребьёвка по правилам FIDE',
        textEn: 'Swiss pairings following FIDE rules',
        description: 'В индивидуальной швейцарке по умолчанию применяется голландская система жеребьёвки FIDE. Подробнее об алгоритмах и условиях их применения — в статье [«Швейцарская система»](/docs/tournaments/swiss/).',
        descriptionEn: 'Individual Swiss tournaments now use the FIDE Dutch pairing system by default. Learn about the algorithms and when they apply in [Swiss system](/docs/en/tournaments/swiss/).',
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '48e897e',
    build: '430',
    date: '2026-10-10',
    title: 'Настройки клуба',
    titleEn: 'Club settings',
    items: [
      {
        text: 'Добавлен раздел «Настройки» клуба',
        textEn: 'Club Settings section added',
        description: 'Настраивайте название и местоположение клуба, выбирайте стили и изображения для его оформления.',
        descriptionEn: 'Set your club’s name and location, and choose styles and images to customise its appearance.',
        roles: ['club-admin'],
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '58f3a09',
    build: '422',
    date: '2026-10-07',
    title: 'Улучшение швейцарской жеребьёвки',
    titleEn: 'Swiss pairing improvements',
    items: [
      {
        text: 'Улучшена жеребьёвка по швейцарской системе',
        textEn: 'Improved Swiss-system pairings',
        description: 'При подборе пар учитываются рейтинги игроков, цветовые предпочтения и повторные переходы в нижние очковые группы. Улучшены формирование случайных команд и подбор соперников в командных матчах.',
        descriptionEn: 'Pairings now take player ratings, colour preferences and repeated moves to lower score groups into account. Random team formation and opponent selection in team matches have also been improved.',
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '031f031',
    build: '419',
    date: '2026-10-06',
    title: 'Фильтрация и сортировка игроков',
    titleEn: 'Player filtering and sorting',
    items: [
      {
        text: 'В список игроков добавлены фильтрация и сортировка',
        textEn: 'Filtering and sorting added to the player list',
        description: 'Настраивайте фильтры и порядок игроков в списке, чтобы сравнивать результаты и просматривать как отдельную, так и общую статистику.',
        descriptionEn: 'Adjust filters and player order to compare results and view both specific and overall statistics.',
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '68c26d4',
    build: '409',
    date: '2026-10-06',
    title: 'Бонусы вне турниров',
    titleEn: 'Bonuses outside tournaments',
    items: [
      {
        text: 'Начисляйте сезонные очки за отдельные активности',
        textEn: 'Award season points for separate activities',
        description: 'На странице сезона появилась кнопка «Бонусы». Создавайте поля для повторяющихся активностей, начисляйте очки одному или нескольким игрокам и просматривайте историю по игрокам, начислениям или событиям. Очки учитываются в лидерборде сезона. Подробнее — в статье [«Бонусы вне турниров»](/docs/seasons/activity-bonuses/).',
        descriptionEn: 'The season page now has a Bonuses button. Create fields for recurring activities, award points to one or several players, and review the history by player, entry or event. Points count towards the season leaderboard. Learn more in [Bonuses outside tournaments](/docs/en/seasons/activity-bonuses/).',
        roles: ['club-admin'],
      },
    ],
  },
  {
    version: '0.2.2',
    commit: 'ef6591c',
    build: '396',
    date: '2026-10-05',
    title: 'Мобильное меню и исправления',
    titleEn: 'Mobile menu and fixes',
    items: [
      {
        text: 'Небольшие правки и фиксы в админ-панели',
        textEn: 'Minor improvements and fixes in the admin panel',
        roles: ['club-admin'],
      },
      {
        text: 'Изменено мобильное меню',
        textEn: 'Mobile menu updated',
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '6bf1d4a',
    build: '394',
    date: '2026-10-05',
    title: 'Финансы и управление оплатами',
    titleEn: 'Finances and payment management',
    items: [
      {
        text: 'Добавлен раздел «Финансы»',
        textEn: 'Finances section added',
        description: `
          В панели управления клуба появился раздел для учёта оплат турниров и продаж абонементов. Просматривайте оплаты и поступления, проверяйте задолженности. Здесь же можете настроить стандартную стоимость участия и отдельные правила для игроков, соответствующих выбранным критериям. Все поступления и начисления можно корректировать вручную. Подробнее — в статье [«Финансы»](/docs/roles/finances/).
        `,
        descriptionEn: `
          The club control panel now includes a section for tracking tournament payments and subscription sales. View payments and receipts, and check outstanding balances. You can also set the standard entry fee and separate rules for players who meet selected criteria. All receipts and charges can be adjusted manually. Learn more in [Finances](/docs/en/roles/finances/).
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Расширено управление оплатой участника турнира',
        textEn: 'Tournament participant payment controls expanded',
        description: `
          В окне оплаты можно указать полученную сумму и дату, назначить индивидуальный взнос, выбрать абонемент и добавить к нему денежную доплату. Подробнее — в разделе [«Оплата участия в турнире»](/docs/tournaments/participants/#oplata-uchastiya-v-turnire).
        `,
        descriptionEn: `
          The payment dialog lets you record the received amount and date, set an individual fee, select a subscription and add an extra cash payment. Learn more in [Tournament entry payments](/docs/en/tournaments/participants/#oplata-uchastiya-v-turnire).
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Добавлены денежные абонементы и учёт их оплаты',
        textEn: 'Balance subscriptions and subscription payment tracking added',
        description: `
          Помимо абонементов по количеству посещений и по сроку доступны абонементы с денежным балансом: из него списывается стоимость участия игрока в турнире. Подробнее — в разделе [«Абонементы»](/docs/roles/finances/#abonementy).
        `,
        descriptionEn: `
          Alongside visit-count and date-based subscriptions, balance subscriptions are now available: the player's tournament entry fee is deducted from the balance. Learn more in [Subscriptions](/docs/en/roles/finances/#abonementy).
        `,
        roles: ['club-admin'],
      },
    ],
  },
  {
    version: '0.2.2',
    commit: 'fcba40e',
    build: '372',
    date: '2026-09-30',
    title: 'SEO и оптимизация загрузки сайта',
    titleEn: 'SEO and website loading optimization',
    items: [
      {
        text: 'Реализовано SEO и оптимизация загрузки сайта',
        textEn: 'Implemented SEO and optimized website loading',
        roles: ['superadmin'],
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '6e80040',
    build: '344',
    date: '2026-09-29',
    title: 'Поддержка английского языка',
    titleEn: 'English language support',
    items: [
      {
        text: 'Добавлен английский язык интерфейса',
        textEn: 'English interface added',
        description: 'Интерфейс сервиса теперь доступен на русском и английском языках.',
        descriptionEn: 'The service interface is now available in Russian and English.',
      },
    ],
  },
  {
    version: '0.2.2',
    commit: '90fc623',
    build: '333',
    date: '2026-09-20',
    title: 'Метки, категории, титулы и оплата игроков',
    titleEn: 'Player labels, categories, titles and payments',
    items: [
      {
        text: 'Добавлены метки и категории игроков',
        textEn: 'Player labels and categories added',
        description: `
          Теперь игроков можно объединять по лигам, группам и другим принятым в клубе признакам, а важные отметки видеть рядом с именами участников. Подробнее — в разделе [«Метки и категории»](/docs/roles/control-panel/#metki-i-kategorii).
        `,
        descriptionEn: `
          Players can now be grouped into leagues, categories and other club-defined groups, with important marks shown next to participant names. Learn more in [Labels and categories](/docs/en/roles/control-panel/#metki-i-kategorii).
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Добавлен учёт оплаты участия в турнирах',
        textEn: 'Tournament entry fee tracking added',
        description: `
          В платных турнирах администратор клуба может отмечать оплату каждого участника. Оплату можно указать вручную или списать посещение с действующего абонемента игрока. Подробнее — в разделе [«Оплата участия в турнире»](/docs/tournaments/participants/#oplata-uchastiya-v-turnire).
        `,
        descriptionEn: `
          Club administrators can track each participant’s payment in tournaments with entry fees. Payment can be recorded manually or deducted from an active subscription. Learn more in [Tournament entry fees](/docs/en/tournaments/participants/#oplata-uchastiya-v-turnire).
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Добавлены правила оплаты и абонементы',
        textEn: 'Payment rules and subscriptions added',
        description: `
          Для игрока можно оформить освобождение от взносов или абонемент на определённое количество турниров либо до выбранной даты. Администратору клуба доступны остаток абонемента, история использований и управление входящими в него турнирами. Подробнее — в разделе [«Оплата участия»](/docs/roles/control-panel/#oplata-uchastiya).
        `,
        descriptionEn: `
          A player can be exempted from entry fees or given a subscription for a set number of tournaments or through a chosen date. Club administrators can view the remaining balance, usage history and included tournaments. Learn more in [Entry fees](/docs/en/roles/control-panel/#oplata-uchastiya).
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Добавлено управление локальными титулами клуба',
        textEn: 'Local club title management added',
        description: `
          Клуб может создавать собственные титулы для игроков — например, отдельно отметить чемпиона клуба. Подробнее — в разделе [«Локальные титулы»](/docs/roles/control-panel/#lokalnye-tituly).
        `,
        descriptionEn: `
          Clubs can create their own player titles, for example to recognize a club champion. Learn more in [Local titles](/docs/en/roles/control-panel/#lokalnye-tituly).
        `,
        roles: ['club-admin'],
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '466dc79',
    build: '326',
    date: '2026-09-14',
    title: 'Управление игроками клуба',
    titleEn: 'Club player management',
    items: [
      {
        text: 'Добавлена таблица игроков клуба',
        textEn: 'Club player table added',
        description: `
          В панели управления клуба появился список игроков клуба. В таблице можно редактировать Отображаемое имя, Имя и Фамилию игрока, а так же назначать и редактировать звания. Подробнее — в [документации по панели управления](/docs/roles/control-panel/#igroki-kluba).
        `,
        descriptionEn: `
          The club control panel now includes a player list. You can edit a player’s display name, first name and last name, and assign or edit titles. Learn more in the [control panel documentation](/docs/en/roles/control-panel/#igroki-kluba).
        `,
        roles: ['club-admin'],
      }
    ],
  },
  {
    version: '0.2.1',
    commit: 'ae7f448',
    build: '317',
    date: '2026-09-10',
    title: 'Добавлены турниры сезона',
    titleEn: 'Season tournaments added',
    items: [
      {
        text: 'На странице сезона появился список турниров',
        textEn: 'Tournament list added to the season page',
        description: `
          Просматривайте турниры сезона, призёров и итоговые таблицы участников.
          Добавлены поиск по названию турнира и игрокам, фильтры и выбор количества турниров на странице.
        `,
        descriptionEn: `
          View season tournaments, prize winners and final participant standings.
          Search by tournament or player name, apply filters and choose how many tournaments appear on each page.
        `,
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '5012791',
    build: '306',
    date: '2026-09-08',
    title: 'Добавлена документация сервиса',
    titleEn: 'Service documentation added',
    items: [
      {
        text: 'Подготовлены подробные инструкции по использованию сервиса',
        textEn: 'Detailed service guides are now available',
        description: `
          В разделе [«Документация»](/docs/) собраны подробные инструкции по использованию сервиса,
          управлению клубами, игроками, турнирами и сезонами, а также ответы на частые вопросы.
        `,
        descriptionEn: `
          The [Documentation](/docs/en/) section contains detailed guides to using the service,
          managing clubs, players, tournaments and seasons, as well as answers to common questions.
        `,
      },
      {
        text: 'Небольшие исправления',
        textEn: 'Minor fixes',
        description: `
          Исправлены отдельные ошибки, обнаруженные в работе сервиса.
        `,
        descriptionEn: `
          Several issues found while using the service have been fixed.
        `,
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '19b4c92',
    build: '291',
    date: '2026-07-31',
    title: 'Настройки турниров и смешанные системы',
    titleEn: 'Tournament settings and mixed systems',
    items: [
      {
        text: 'Добавлено редактирование настроек турнира',
        textEn: 'Tournament settings can now be edited',
        description: `
          После создания турнира его настройки можно открыть из меню {icon:bi-three-dots-vertical} — пункт «Редактировать турнир».
        `,
        descriptionEn: `
          After creating a tournament, open its settings from the {icon:bi-three-dots-vertical} menu and select “Edit tournament”.
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Добавлена возможность начать плей-офф по ходу турнира',
        textEn: 'Playoffs can now be started during a tournament',
        description: `
          Если турнир изначально начат по швейцарской системе или круговику, плей-офф можно добавить через дополнительное выпадающее меню кнопки «Завершить турнир».
        `,
        descriptionEn: `
          If a tournament starts as Swiss or round robin, a playoff can be added through the extra menu next to “Finish tournament”.
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Расширены возможности смешанной системы',
        textEn: 'Mixed-system options expanded',
        description: `
          При создании смешанного турнира в качестве квалификации теперь можно выбрать швейцарскую систему или круговик.
        `,
        descriptionEn: `
          When creating a mixed tournament, you can now use either Swiss or round robin for qualification.
        `,
        roles: ['club-admin'],
      },
    ],
  },
  {
    version: '0.2.1',
    commit: 'b158c1d',
    build: '283',
    date: '2026-07-20',
    title: 'Привязка changelog к коммитам',
    titleEn: 'Changelog entries linked to commits',
    items: [
      {
        text: 'Добавлена фиксация хэша коммита для записей changelog',
        textEn: 'Commit hashes added to changelog entries',
        description: `
          Для каждой новой записи changelog можно указать хэш коммита с реализованными изменениями.
          Хэш подставляется отдельной командой перед деплоем.
        `,
        descriptionEn: `
          Each new changelog entry can include the commit hash containing its changes.
          The hash is inserted by a dedicated command before deployment.
        `,
        roles: ['superadmin'],
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '40f4440',
    build: '279',
    date: '2026-07-18',
    title: 'Турниры, ссылки и сезоны',
    titleEn: 'Tournaments, links and seasons',
    items: [
      {
        text: 'Улучшено создание турниров',
        textEn: 'Tournament creation improved',
        description: `
          При создании турнира теперь можно указать время начала.
        `,
        descriptionEn: `
          A tournament start time can now be set during creation.
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Расширен экспорт результатов',
        textEn: 'Results export expanded',
        description: `
          Экспорт PNG с подготовленной таблицей или сеткой теперь доступен и для плей-офф.
        `,
        descriptionEn: `
          Prepared standings and bracket PNG exports are now available for playoffs as well.
        `,
      },
      {
        text: 'Обновлены публичные ссылки',
        textEn: 'Public links updated',
        description: `
          У турниров появились короткие ссылки, а адреса клуба, турнира и сезона стали компактнее. Старые ссылки продолжают работать.
        `,
        descriptionEn: `
          Tournaments now have short links, and club, tournament and season addresses are more compact. Existing links continue to work.
        `,
      },
      {
        text: 'Управление статусом сезона из меню',
        textEn: 'Season status management added to the menu',
        description: `
          Сезон можно начать, завершить, возобновить или вернуть в статус «Создан».
        `,
        descriptionEn: `
          A season can be started, finished, resumed or returned to Created status.
        `,
        roles: ['club-admin'],
      },
      {
        text: 'Небольшие визуальные улучшения',
        textEn: 'Minor visual improvements',
        description: `
          Обновлено отображение времени, видов шахмат и отдельных элементов интерфейса.
        `,
        descriptionEn: `
          Time controls, chess variants and several interface elements now display more clearly.
        `,
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '933508a',
    build: '276',
    date: '2026-07-13',
    title: 'Замены игроков в турнире',
    titleEn: 'Tournament player replacements',
    items: [
      {
        text: 'Добавлена возможность замены игрока в турнире',
        textEn: 'Tournament player replacement added',
        description: `
          Теперь можно заменить участника так, чтобы новый игрок продолжил выступление вместо прежнего.
          История уже сыгранных партий сохраняется за игроком, который играл их фактически.
        `,
        descriptionEn: `
          A participant can now be replaced so another player continues in the same slot.
          Completed games remain associated with the player who actually played them.
        `,
        roles: ['authenticated'],
      },
    ],
  },
  {
    version: '0.2.1',
    commit: '933508a',
    build: '276',
    date: '2026-07-13',
    title: 'Ручное управление партиями + bugfix',
    titleEn: 'Manual game management and fixes',
    items: [
      {
        text: 'Добавлено ручное создание и редактирование партий в жеребьевке турнира',
        textEn: 'Manual game creation and editing added to pairings',
        description: `
          В меню жеребьевки и в меню тура появился пункт для ручного добавления матча.
          Созданные партии можно редактировать прямо из меню партии.
        `,
        descriptionEn: `
          The pairings and round menus now include an option to add a game manually.
          Created games can be edited directly from the game menu.
        `,
        roles: ['authenticated'],
      },
      {
        text: 'Исправлены ошибки с рейтингами в товарищеских турнирах и при начале жеребьевки',
        textEn: 'Rating and pairing initialization issues fixed',
        description: `
          При создании товарищеского турнира рейтинги подгружаются с учетом типа контроля и вида шахмат.
          Также добавлена обработка ошибки загрузки модуля жеребьевки с понятным уведомлением.
        `,
        descriptionEn: `
          Friendly tournaments now load ratings for the selected time control and chess variant.
          Pairing module loading errors now show a clear notification.
        `,
        roles: ['authenticated'],
      },
      {
        text: 'Небольшие визуальные улучшения',
        textEn: 'Minor visual improvements',
        description: `
          Обновлены отдельные элементы интерфейса: меню, виджеты главной страницы и пр.
        `,
        descriptionEn: `
          Several interface elements were updated, including menus and homepage widgets.
        `,
      },
    ],
  },
  {
    version: '0.2.0',
    commit: '85ba49f',
    build: '262',
    date: '2026-06-18',
    title: 'Регистрация после создания турнира',
    titleEn: 'Registration after tournament creation',
    items: [
      {
        text: 'Добавлена возможность регистрации игроков после создания турнира',
        textEn: 'Players can now be registered after tournament creation',
        description: `
          Теперь можно создать турнир без регистрации участников: он появится в запланированных.
          После этого со страницы турнира можно добавить игроков и команды.
        `,
        descriptionEn: `
          A tournament can now be created without registering participants and will appear as scheduled.
          Players and teams can then be added from the tournament page.
        `,
      },
    ],
  },
  {
    version: '0.2.0',
    commit: '5702ef3',
    build: '257',
    date: '2026-06-14',
    title: 'Обновлен дизайн сайта',
    titleEn: 'Website design updated',
    items: [
      {
        text: 'Обновлен дизайн сайта',
        textEn: 'Website design updated',
        description: `
          Добавлены 4 варианта цветовых схем. Переключать оформление можно через кнопку темы.
          Доступные схемы: {icon:bi-brightness-high|#d98224} светлая, {icon:bi-moon|#6f7ea8} темная, {icon:bi-cup-hot|#b36b18} пивная и {icon:bi-circle-half|#000000} контрастная.
        `,
        descriptionEn: `
          Four color themes have been added. Use the theme button to switch between them.
          Available themes: {icon:bi-brightness-high|#d98224} light, {icon:bi-moon|#6f7ea8} dark, {icon:bi-cup-hot|#b36b18} warm and {icon:bi-circle-half|#000000} high contrast.
        `,
      },
    ],
  },
  {
    version: '0.1.0',
    commit: '03df376',
    build: '252',
    date: '2026-05-23',
    title: 'Добавлена панель уведомлений',
    titleEn: 'Notification panel added',
    items: [
      {
        text: 'Добавлена панель уведомлений',
        textEn: 'Notification panel added',
        description: `Здесь будут отображаться уведомления о новых функциях, изменениях и важных событиях в приложении. Панель доступна через кнопку "колокольчика" в меню.`,
        descriptionEn: `Notifications about new features, changes and important application events appear here. Open the panel using the bell button in the menu.`,
      }
    ],
  },
  {
    version: '0.1.0',
    commit: '85ee136',
    build: '251',
    date: '2026-05-22',
    title: 'Командные турниры и администрирование',
    titleEn: 'Team tournaments and administration',
    items: [
      {
        text: 'Командные турниры и новые виды шахмат',
        textEn: 'Team tournaments and new chess variants',
        description: `
          Добавлена поддержка {success:командных соревнований}, фиксированных и случайных команд, и новых вариантов шахмат: добавлены \`Шведки\`, \`Парные шахматы: 2x2\`, \`Голова-рука\` и возможность команд в классическом режиме и Chess960.`,
        descriptionEn: `
          Support has been added for {success:team competitions}, fixed and random teams, and new chess variants: \`Bughouse\`, \`Doubles: 2x2\`, \`Hand and Brain\`, plus team play in Standard and Chess960.`,
      },
      {
        text: 'Сетка плей-офф',
        textEn: 'Playoff bracket',
        description: `
          Обновлены подписи, отображение финальных стадий, нижней сетки
          и визуальное выделение результатов. Поддержаны командные режимы.
        `,
        descriptionEn: `
          Labels, final stages, the lower bracket and result highlighting have been updated.
          Team modes are supported.
        `,
      },
      {
        text: 'Администрирование и рейтинги',
        textEn: 'Administration and ratings',
        description: `
          Добавлены инструменты пересчета рейтингов, обновлены расчеты
          и структура кэшей турниров и сезонов.
        `,
        descriptionEn: `
          Rating recalculation tools have been added, and calculation logic and tournament and season cache structures have been updated.
        `,
        roles: ['superadmin'],
      },
    ],
  }
];
